import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../ui/GlassComponents';

const OnboardingWelcome = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [isCompleting, setIsCompleting] = useState(false);

  if (!isOpen) return null;

  const handleGetStarted = async () => {
    setIsCompleting(true);
    
    try {
      // Mark onboarding as completed so popup doesn't show again
      await axios.put('/api/users/me', {
        onboardingCompleted: true
      });
      
      // Refresh user data to reflect the change
      await refreshUser();
      
      // Close the popup
      onClose();
      
      // Small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to team settings
      navigate('/dashboard/team-settings', { replace: true });
      
      toast.success('Welcome to LeaveWise! Let\'s get your organisation set up.');
      
    } catch (error) {
      console.error('Error updating onboarding status:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSkipForNow = async () => {
    setIsCompleting(true);
    try {
      // Mark onboarding as completed so popup doesn't show again
      await axios.put('/api/users/me', {
        onboardingCompleted: true
      });
      
      // Refresh user data to reflect the change
      await refreshUser();
      
      // Close the popup
      onClose();
      
      toast.success('You can configure your organisation settings anytime from Team Settings.');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl">
        <GlassCard className="p-8 text-center">
          {/* Welcome Header */}
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <img 
                src="/images/lwlogo.png" 
                alt="LeaveWise Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome to LeaveWise! 🎉
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Thank you for choosing LeaveWise to manage your team's leave requests.
            </p>
          </div>

          {/* Setup Instructions */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Let's get your organisation set up!
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                To get the most out of LeaveWise, please take 2 minutes to configure your organisation settings. 
                This will ensure your team has the best possible experience.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">General Settings</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Organisation name, logo, and basic info</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Work Hours</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Define your working days and hours</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Leave Types</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Set up vacation, sick leave, and custom types</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Business Year</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Configure your leave year dates</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <GlassButton
              onClick={handleGetStarted}
              disabled={isCompleting}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCompleting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Setting up...</span>
                </div>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  Take me to Team Settings
                </>
              )}
            </GlassButton>
            
            <GlassButton
              onClick={handleSkipForNow}
              disabled={isCompleting}
              variant="secondary"
              className="px-6 py-3 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors duration-200"
            >
              I'll do this later
            </GlassButton>
          </div>

          {/* Footer Note */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You can always access these settings later from the Team Settings page in your navigation menu.
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default OnboardingWelcome;
